// Progressive result panel shown on BuildDeck while the agent's final
// answer is streaming in. Driven by parseProgressive — renders the
// decklist as soon as the Arena fences close, then a grid of section
// tiles whose bodies fill in live and whose statuses flip from pending
// (shimmer) → streaming (live-text + caret) → done.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Mountain,
  Sparkles,
  Star,
  Swords,
  Target,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import type { CardMeta, Deck } from "../lib/api";
import DeckView from "./DeckView";
import Markdown from "./Markdown";
import {
  parseProgressive,
  SECTION_ORDER,
  type SectionKey,
} from "../lib/progressive-sections";
import { useMyUsername } from "../lib/useMyUsername";
import { cn } from "../lib/cn";

interface Props {
  /** Raw assistant-text buffer — concatenated assistant_text_delta values
   *  for the final (answer-producing) turn. */
  buffer: string;
  /** Card metadata map keyed by lowercase name. Passed through to DeckView
   *  so images / mana costs render while the section tiles stream. */
  meta?: Record<string, CardMeta>;
  /** Slug of the saved deck once `deck_saved` arrives. When present, the
   *  panel surfaces a "View saved deck" CTA so the user can jump to the
   *  permanent detail page without being auto-redirected mid-stream. */
  savedDeckId?: string | null;
  /** Known deck supplied by upload flows. Build flows omit this because
   *  their assistant answer streams the decklist before the analysis tiles. */
  seedDeck?: Deck | null;
  /** Optional subset/order for flows whose prompt only asks for some tiles. */
  sectionOrder?: SectionKey[];
  /** Accepted final output: finish present tiles and hide absent placeholders. */
  complete?: boolean;
}

const ICON_PROPS = { size: 14, strokeWidth: 1.75 } as const;

const SECTION_META: Record<
  SectionKey,
  { title: string; icon: ReactNode; accent?: "accent" | "warn" | "muted" }
> = {
  winCondition: {
    title: "Win Condition",
    icon: <Target {...ICON_PROPS} />,
    accent: "accent",
  },
  keyInteractions: {
    title: "Key Interactions",
    icon: <Sparkles {...ICON_PROPS} />,
    accent: "accent",
  },
  manaBase: { title: "Mana Base", icon: <Mountain {...ICON_PROPS} /> },
  sideboardGuide: { title: "Sideboard Guide", icon: <Swords {...ICON_PROPS} /> },
  weaknesses: {
    title: "Weaknesses",
    icon: <ShieldCheck {...ICON_PROPS} />,
    accent: "warn",
  },
  exemplarsConsulted: {
    title: "Exemplars Consulted",
    icon: <Users {...ICON_PROPS} />,
    accent: "muted",
  },
  honorableMentions: {
    title: "Honorable Mentions",
    icon: <Star {...ICON_PROPS} />,
  },
};

export default function StreamingDeckResult({
  buffer,
  meta,
  savedDeckId,
  seedDeck = null,
  sectionOrder = SECTION_ORDER,
  complete = false,
}: Props) {
  const state = useMemo(() => parseProgressive(buffer, seedDeck, complete), [buffer, seedDeck, complete]);
  const myUsername = useMyUsername();

  if (!state.hasDeck) {
    if (complete) return null;
    // Nothing useful parsed yet. Render a lightweight placeholder so the
    // user has a visible anchor while the agent finishes its turn.
    return (
      <section className="surface p-5 space-y-3">
        <div className="eyebrow flex items-center gap-2">
          <BookOpen {...ICON_PROPS} />
          Result
        </div>
        <div className="text-sm text-fg-subtle">
          Drafting the list…
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-5">
      {state.deck && (
        <DeckView deck={state.deck} meta={meta} />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {sectionOrder.map((key) => {
          const m = SECTION_META[key];
          const { status, body } = state.sections[key];
          if (complete && status === "pending") return null;
          // Exemplars Consulted is conditional — only written when the
          // agent actually called get_archetype_exemplars. Hiding the
          // tile while pending avoids a forever-shimmer skeleton at the
          // bottom of the grid when the toggle was off.
          if (key === "exemplarsConsulted" && status === "pending") return null;
          return (
            <SectionTile
              key={key}
              title={m.title}
              icon={m.icon}
              accent={m.accent}
              status={status}
              body={body}
            />
          );
        })}
      </div>

      {savedDeckId && myUsername && (
        <div className="flex items-center justify-between gap-3 surface p-4 border-accent/30">
          <div className="text-sm text-fg">
            Saved. Your deck is live at{" "}
            <code className="font-mono text-[12px] text-fg-muted">
              /decks/{myUsername}/{savedDeckId}
            </code>
          </div>
          <Link
            to={`/decks/${myUsername}/${savedDeckId}?fromBuild=1`}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent/20 hover:bg-accent/30 text-accent px-3 py-1.5 text-xs font-medium transition-colors"
          >
            View saved deck
          </Link>
        </div>
      )}
    </div>
  );
}

function SectionTile({
  title,
  icon,
  accent,
  status,
  body,
}: {
  title: string;
  icon: ReactNode;
  accent?: "accent" | "warn" | "muted";
  status: "pending" | "streaming" | "done";
  body: string;
}) {
  return (
    <section className="surface p-4 min-h-[6rem]">
      <header
        className={cn(
          "flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider font-medium mb-2",
          accent === "accent" && "text-accent",
          accent === "warn" && "text-warn",
          (!accent || accent === "muted") && "text-fg-subtle",
        )}
      >
        <span className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </span>
        {status === "pending" && (
          <span className="font-mono normal-case tracking-normal text-fg-subtle">
            waiting…
          </span>
        )}
        {status === "streaming" && (
          <span className="inline-flex items-center gap-1.5 font-mono normal-case tracking-normal text-accent">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
            </span>
            streaming
          </span>
        )}
      </header>
      <div className="text-sm leading-relaxed text-fg space-y-2">
        {status === "pending" ? (
          <SkeletonLines />
        ) : body ? (
          <Markdown text={body} />
        ) : (
          <div className="text-fg-subtle text-xs">…</div>
        )}
      </div>
    </section>
  );
}

function SkeletonLines() {
  return (
    <div className="space-y-2" aria-hidden>
      <div className="skeleton h-3 w-[85%] rounded-sm" />
      <div className="skeleton h-3 w-[70%] rounded-sm" />
      <div className="skeleton h-3 w-[55%] rounded-sm" />
    </div>
  );
}
